import { EmailTemplate } from '@spike/email-marketing-model';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent } from 'react';
import styles from './EmailTemplateCard.module.scss';

interface EmailTemplateCardProps {
    template: EmailTemplate;
    /**
     * Optional callback for picker mode. When provided, clicking "Use Template"
     * passes the template to the caller (typically the campaign wizard) instead
     * of being inert. Standalone gallery views can omit it.
     *
     * NOTE: still optional for back-compat with legacy call sites. New
     * consumers should always wire it; making it required is a follow-up.
     */
    onSelectTemplate?: (template: EmailTemplate) => void;
    /**
     * Opens the template in a full-size preview modal. Required so every
     * consumer of the Card has to wire the action explicitly — prevents the
     * silent "Preview button does nothing" bug from regressing.
     */
    onPreviewTemplate: (template: EmailTemplate) => void;
}

const EmailTemplateCard: FunctionComponent<EmailTemplateCardProps> = ({
    template,
    onSelectTemplate,
    onPreviewTemplate
}) => {
    const handleUseTemplate = () => {
        if (onSelectTemplate) {
            onSelectTemplate(template);
        }
    };

    const handlePreview = () => {
        onPreviewTemplate(template);
    };

    return (
        <div className={styles.templateCard}>
            <div className={styles.templateImage}>
                {template.html ? (
                    <iframe
                        className={styles.templateImageIframe}
                        title={template.name}
                        srcDoc={template.html}
                        sandbox=""
                        scrolling="no"
                    />
                ) : (
                    <div className={styles.templateImagePlaceholder} />
                )}
            </div>

            <div className={styles.templateInfo}>
                <h4 className={styles.templateTitle}>{template.name}</h4>
            </div>

            <div className={styles.templateActions}>
                <Button
                    label="Preview"
                    variant="outlined"
                    className={styles.actionButton}
                    onClick={handlePreview}
                />
                <Button
                    label="Use Template"
                    variant="primary"
                    className={styles.actionButton}
                    onClick={handleUseTemplate}
                />
            </div>
        </div>
    );
};

export default EmailTemplateCard;
